/**
 * @file HeightModeField.tsx
 * @description Высота секции — четыре режима схемами (phase19.16, партия
 * SB120, этап 1; uiux-sb120-stage1 «Дополнение» §2.1, вопрос 7; данные
 * db-design-sb120-stage1 §1, решение (а): «Числом» — px, «Экран» — % окна
 * без шапки).
 *
 * ```
 * ┌──────┐┌──────┐┌──────┐┌──────┐
 * │ ▭ Aa ││ ▣    ││ ▭ 600││ ▯    │   По фото: 1280 ÷ 1,78 = 720 px
 * │По со-││По    ││Числом││Экран │   Телефон: ↺ как на ПК
 * └──────┘└──────┘└──────┘└──────┘
 * ```
 *
 * ПК — `look.height`, телефон — `look.heightPhone` (нет ключа — как на ПК;
 * «По фото» на телефоне — «По кадру телефона 4:5» или фото целиком).
 * «По фото» недоступно без фото фона. Текст главнее высоты: высота — нижняя
 * граница, содержимое растит блок.
 */

import { SITE_THEME_LIMITS, type SectionHeight, type SectionLook } from "@chaiberi/shared";
import { RotateCcw } from "lucide-react";

import { Button } from "../../../ui/button";
import { FieldHint } from "../../../ui/settings-groups";
import { OptionCards } from "../../../site-view/OptionCards";
import { Segmented } from "../../../site-view/Segmented";
import { LookWarningNote } from "../fields/LookWarningNote";
import { useSectionForm } from "../section-form-context";
import { HeightScheme } from "./HeightScheme";
import { HeightValueField } from "./HeightValueField";
import { heightLabel } from "../../../../lib/site-builder/look-summary";
import { HEIGHT_MODES, PHONE_FRAME_ASPECT, photoHeightCalc, type Device, type HeightKind } from "../../../../lib/site-builder/look-size";

interface Props {
  look: SectionLook;
  device: Device;
  onChange: (look: SectionLook) => void;
  /** Пропорции файла фона на этом экране (своё фото телефона — его); null — неизвестны. */
  fileAspect: number | null;
}

const PHOTO_PHONE = [
  { value: "frame" as const, label: "Кадр 4:5" },
  { value: "file" as const, label: "Фото целиком" },
];

export function HeightModeField({ look, device, onChange, fileAspect }: Props) {
  const phone = device === "phone";
  const own = !phone || look.heightPhone !== undefined;
  const current = phone ? (look.heightPhone ?? look.height) : look.height;
  const hasPhoto = look.bg.kind === "media";
  const setHeight = (height: SectionHeight | undefined) => {
    if (!phone && height) onChange({ ...look, height });
    else if (height) onChange({ ...look, heightPhone: height });
    else {
      const next = { ...look };
      delete next.heightPhone;
      onChange(next);
    }
  };
  const pick = (kind: HeightKind) => {
    const mode = HEIGHT_MODES.find((m) => m.kind === kind);
    if (!mode) return;
    // На телефоне «По фото» — «По кадру телефона 4:5» (вопрос 7).
    setHeight(phone && kind === "photo" ? { kind: "photo", aspect: PHONE_FRAME_ASPECT } : mode.make());
  };
  const density = useSectionForm().theme?.spacing.density ?? SITE_THEME_LIMITS.density.default;
  const calc = current.kind === "photo" ? photoHeightCalc(look, device, current.aspect ?? fileAspect, density) : null;

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="flex min-h-8 items-center justify-between gap-2">
        <span className="type-body font-medium text-on-surface">{phone ? "Высота на телефоне" : "Высота"}</span>
        {phone && own ? (
          <Button variant="ghost" size="sm" className="h-12 can-hover:h-8" onClick={() => setHeight(undefined)} aria-label="Высота на телефоне: как на ПК">
            <RotateCcw aria-hidden />
            как на ПК
          </Button>
        ) : phone ? (
          <span className="type-caption text-on-surface-variant">как на ПК: {heightLabel(look.height)}</span>
        ) : null}
      </div>
      <OptionCards
        aria-label={phone ? "Высота блока на телефоне" : "Высота блока"}
        columns={4}
        value={current.kind}
        onChange={pick}
        options={HEIGHT_MODES.map((m) => ({
          value: m.kind,
          label: phone && m.kind === "photo" ? "По фото / кадру" : m.label,
          preview: <HeightScheme kind={m.kind} />,
          disabled: m.kind === "photo" && !hasPhoto && current.kind !== "photo",
          hint: m.kind === "photo" && !hasPhoto ? "Сначала выберите фото в «Фоне»" : undefined,
        }))}
      />
      {current.kind === "photo" && phone ? (
        <Segmented
          aria-label="Высота по фото на телефоне"
          options={PHOTO_PHONE}
          value={current.aspect === undefined ? "file" : "frame"}
          onChange={(v) => setHeight(v === "file" ? { kind: "photo" } : { kind: "photo", aspect: PHONE_FRAME_ASPECT })}
        />
      ) : null}
      {current.kind === "photo" ? (
        <FieldHint className="mt-0">
          {!hasPhoto ? "Фото фона нет — на сайте высота по содержимому." : calc ? `${calc.text} — фото видно целиком.` : "Фото целиком — высота по пропорции файла."} Это нижняя граница: если текст выше — блок вырастет, фото обрежется вокруг точки фокуса.
        </FieldHint>
      ) : null}
      {current.kind === "number" || current.kind === "screen" ? (
        <HeightValueField height={current} path={phone ? "look.heightPhone.value" : "look.height.value"} onChange={setHeight} ensureOwn={own ? undefined : () => setHeight({ ...current })} />
      ) : null}
      {phone && look.heightPhone?.kind === "content" && look.height.kind !== "content" ? (
        <Button variant="outline" className="self-start" onClick={() => setHeight(undefined)}>
          Сделать как на ПК
        </Button>
      ) : null}
      <LookWarningNote paths={phone || !look.heightPhone ? ["look.heightPhone", "look.height"] : []} />
    </div>
  );
}
